import { c as _c } from "react-compiler-runtime";
import { countries } from "countries-list";
import { Check, ChevronDown, Info, Search } from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { type Control, FieldValues, Path, useController, useFormContext } from "react-hook-form";
import { type Country, getCountries, getCountryCallingCode } from "react-phone-number-input/input";
import { ErrorMessage, HintMessage, Label, useFieldErrorVisibility, useFieldIds } from "@/components/form";
import { Text } from "@/components/primitives";
import { Input } from "@/components/ui/input";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useTranslation } from "@/core/i18n";
import { cn } from "@/core/utils";
import { formatPhone } from "@/core/utils/helpers.utils";
import { normalizePhoneInputToE164IfPossible } from "@/core/utils/phone.utils";
import { SIGNUP_FIELD_COUNTRY_BUTTON_CLASSNAME, SIGNUP_FIELD_INPUT_CLASSNAME, SIGNUP_FIELD_LABEL_CLASSNAME } from "@/features/signup/components/shared/signupFormStyles";
function FlagEmoji({
  country,
  testId
}: {
  readonly country: Country;
  readonly testId?: string;
}): React.ReactElement {
  return React.createElement("span", {
    "aria-hidden": true,
    "className": "type-body-md",
    "data-testid": testId
  }, toFlagEmoji(country));
}
export type InternationalPhoneCountry = {
  readonly id: string;
  /** ISO alpha-2 code. Shown in the closed trigger for the signup appearance. */
  readonly label: string;
  /** Human-readable country name, shown in the menu — nobody picks a country by ISO code. */
  readonly name: string;
  readonly country: Country;
  readonly dialCode: string;
};
const PRIORITY_COUNTRIES: readonly Country[] = ["US", "CA"];
function toFlagEmoji(country: Country): string {
  if (!/^[A-Z]{2}$/.test(country)) {
    return "";
  }
  const [firstCode, secondCode] = country;
  const OFFSET = 127397;
  return String.fromCodePoint(firstCode.charCodeAt(0) + OFFSET, secondCode.charCodeAt(0) + OFFSET);
}
function createDefaultInternationalPhoneCountries(): readonly InternationalPhoneCountry[] {
  const allCountries = getCountries();
  const prioritySet = new Set(PRIORITY_COUNTRIES);
  const orderedCountries = [...PRIORITY_COUNTRIES.filter(country => allCountries.includes(country)), ...allCountries.filter(country => !prioritySet.has(country))];
  return orderedCountries.map(country => ({
    id: country.toLowerCase(),
    label: country,
    // `countries-list` covers ISO-assigned countries; react-phone-number-input also dials a few
    // territories it has no entry for (Ascension, Tristan da Cunha). Fall back to the code rather
    // than dropping a dialable country from the list.
    name: countries[country as keyof typeof countries]?.name ?? country,
    country,
    dialCode: `+${getCountryCallingCode(country)}`
  }));
}

/** Matches a country on its name, ISO code, or dial code, so "mex", "MX" and "52" all find Mexico. */
function matchesCountryQuery(country: InternationalPhoneCountry, query: string): boolean {
  const normalized = query.trim().toLowerCase();
  if (normalized.length === 0) return true;
  const digits = normalized.replace(/\D/g, "");
  return country.name.toLowerCase().includes(normalized) || country.label.toLowerCase().includes(normalized) || digits.length > 0 && country.dialCode.replace(/\D/g, "").startsWith(digits);
}
export const DEFAULT_INTERNATIONAL_PHONE_COUNTRIES = createDefaultInternationalPhoneCountries();
function formatPhoneInputValue(rawValue: unknown, country: Country): string {
  if (typeof rawValue !== "string") {
    return "";
  }
  if (country === "US" || country === "CA") {
    return formatPhone(rawValue);
  }
  return rawValue;
}
type FieldAppearance = "default" | "signup";
type RHFProps<T extends FieldValues> = {
  readonly appearance?: FieldAppearance;
  readonly name: Path<T>;
  readonly control?: Control<T>;
  readonly label: string;
  readonly hint?: string;
  readonly tooltip?: string;
  readonly placeholder?: string;
  readonly countries?: readonly InternationalPhoneCountry[];
  readonly defaultCountryId?: string;
  readonly hideCountrySelector?: boolean;
  readonly onCountryChange?: (country: InternationalPhoneCountry) => void;
  readonly onInputChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
};
function RHF(t0) {
  const $ = _c(130);
  const {
    appearance: t1,
    name,
    control,
    label,
    hint,
    tooltip,
    placeholder,
    countries: t2,
    defaultCountryId,
    hideCountrySelector: t3,
    onCountryChange,
    onInputChange
  } = t0;
  const appearance = t1 === undefined ? "default" : t1;
  const countries = t2 === undefined ? DEFAULT_INTERNATIONAL_PHONE_COUNTRIES : t2;
  const hideCountrySelector = t3 === undefined ? false : t3;
  const {
    trigger
  } = useFormContext();
  let t4;
  if ($[0] !== control || $[1] !== name) {
    t4 = {
      name,
      control
    };
    $[0] = control;
    $[1] = name;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const {
    field,
    fieldState
  } = useController(t4);
  const showError = useFieldErrorVisibility(fieldState, control);
  const fieldIds = useFieldIds(name);
  const countryMenuRef = useRef(null);
  const lastReportedCountryIdRef = useRef(null);
  const isSignupAppearance = appearance === "signup";
  const safeCountries = countries.length > 0 ? countries : DEFAULT_INTERNATIONAL_PHONE_COUNTRIES;
  let t5;
  if ($[3] !== defaultCountryId || $[4] !== safeCountries) {
    t5 = safeCountries.find(country => country.id === defaultCountryId) ?? safeCountries[0];
    $[3] = defaultCountryId;
    $[4] = safeCountries;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const fallbackCountry = t5;
  const {
    t
  } = useTranslation();
  let t6;
  if ($[6] !== t) {
    t6 = t("common.phoneField.countrySearch");
    $[6] = t;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const countrySearchLabel = t6;
  let t7;
  if ($[8] !== t) {
    t7 = t("common.phoneField.noCountryMatch");
    $[8] = t;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const countryEmptyLabel = t7;
  const [selectedCountry, setSelectedCountry] = useState(fallbackCountry);
  const [isCountryMenuOpen, setCountryMenuOpen] = useState(false);
  const [countryQuery, setCountryQuery] = useState("");
  const [activeOptionIndex, setActiveOptionIndex] = useState(0);
  const countrySearchRef = useRef(null);
  const countryListRef = useRef(null);
  const countryTriggerRef = useRef(null);
  let t8;
  if ($[10] !== countryQuery || $[11] !== safeCountries) {
    let t9;
    if ($[13] !== countryQuery) {
      t9 = country_0 => matchesCountryQuery(country_0, countryQuery);
      $[13] = countryQuery;
      $[14] = t9;
    } else {
      t9 = $[14];
    }
    t8 = safeCountries.filter(t9);
    $[10] = countryQuery;
    $[11] = safeCountries;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  const visibleCountries = t8;
  let t9;
  if ($[15] !== safeCountries || $[16] !== selectedCountry.id) {
    t9 = () => {
      const selectedIndex = safeCountries.findIndex(country_1 => country_1.id === selectedCountry.id);
      setCountryQuery("");
      setActiveOptionIndex(selectedIndex >= 0 ? selectedIndex : 0);
      setCountryMenuOpen(true);
    };
    $[15] = safeCountries;
    $[16] = selectedCountry.id;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  const openCountryMenu = t9;
  let t10;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = t11 => {
      const {
        restoreFocus
      } = t11;
      setCountryMenuOpen(false);
      setCountryQuery("");
      if (restoreFocus) {
        countryTriggerRef.current?.focus();
      }
    };
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  const closeCountryMenu = t10;
  let t11;
  let t12;
  if ($[19] !== onCountryChange || $[20] !== selectedCountry) {
    t11 = () => {
      if (!onCountryChange) {
        return;
      }
      if (lastReportedCountryIdRef.current === selectedCountry.id) {
        return;
      }
      lastReportedCountryIdRef.current = selectedCountry.id;
      onCountryChange(selectedCountry);
    };
    t12 = [onCountryChange, selectedCountry];
    $[19] = onCountryChange;
    $[20] = selectedCountry;
    $[21] = t11;
    $[22] = t12;
  } else {
    t11 = $[21];
    t12 = $[22];
  }
  useEffect(t11, t12);
  let t13;
  let t14;
  if ($[23] !== hideCountrySelector) {
    t13 = () => {
      if (hideCountrySelector) {
        return;
      }
      const handleClickOutside = event => {
        if (countryMenuRef.current && !countryMenuRef.current.contains(event.target as Node)) {
          setCountryMenuOpen(false);
          setCountryQuery("");
        }
      };
      window.addEventListener("mousedown", handleClickOutside);
      return () => window.removeEventListener("mousedown", handleClickOutside);
    };
    t14 = [hideCountrySelector];
    $[23] = hideCountrySelector;
    $[24] = t13;
    $[25] = t14;
  } else {
    t13 = $[24];
    t14 = $[25];
  }
  useEffect(t13, t14);
  let t15;
  if ($[26] !== field) {
    t15 = country_2 => {
      setSelectedCountry(country_2);
      closeCountryMenu({
        restoreFocus: true
      });
      field.onChange(formatPhoneInputValue(field.value, country_2.country));
    };
    $[26] = field;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  const handleCountrySelect = t15;
  let t16;
  let t17;
  if ($[28] !== isCountryMenuOpen) {
    t16 = () => {
      if (isCountryMenuOpen) {
        countrySearchRef.current?.focus();
      }
    };
    t17 = [isCountryMenuOpen];
    $[28] = isCountryMenuOpen;
    $[29] = t16;
    $[30] = t17;
  } else {
    t16 = $[29];
    t17 = $[30];
  }
  useEffect(t16, t17);
  let t18;
  let t19;
  if ($[31] !== activeOptionIndex || $[32] !== isCountryMenuOpen) {
    t18 = () => {
      if (!isCountryMenuOpen) {
        return;
      }
      countryListRef.current?.querySelector(`[data-option-index="${activeOptionIndex}"]`)?.scrollIntoView({
        block: "nearest"
      });
    };
    t19 = [isCountryMenuOpen, activeOptionIndex];
    $[31] = activeOptionIndex;
    $[32] = isCountryMenuOpen;
    $[33] = t18;
    $[34] = t19;
  } else {
    t18 = $[33];
    t19 = $[34];
  }
  useEffect(t18, t19);
  let t20;
  if ($[35] !== activeOptionIndex || $[36] !== handleCountrySelect || $[37] !== visibleCountries) {
    t20 = event_0 => {
      if (event_0.key === "Escape") {
        event_0.preventDefault();
        closeCountryMenu({
          restoreFocus: true
        });
        return;
      }
      if (visibleCountries.length === 0) {
        return;
      }
      bb100: switch (event_0.key) {
        case "ArrowDown":
          {
            event_0.preventDefault();
            setActiveOptionIndex(index_0 => (index_0 + 1) % visibleCountries.length);
            break bb100;
          }
        case "ArrowUp":
          {
            event_0.preventDefault();
            setActiveOptionIndex(index => (index - 1 + visibleCountries.length) % visibleCountries.length);
            break bb100;
          }
        case "Home":
          {
            event_0.preventDefault();
            setActiveOptionIndex(0);
            break bb100;
          }
        case "End":
          {
            event_0.preventDefault();
            setActiveOptionIndex(visibleCountries.length - 1);
            break bb100;
          }
        case "Enter":
          {
            event_0.preventDefault();
            const country_3 = visibleCountries[activeOptionIndex];
            if (country_3) {
              handleCountrySelect(country_3);
            }
            break bb100;
          }
        default:
      }
    };
    $[35] = activeOptionIndex;
    $[36] = handleCountrySelect;
    $[37] = visibleCountries;
    $[38] = t20;
  } else {
    t20 = $[38];
  }
  const handleCountrySearchKeyDown = t20;
  const countryButtonClassName = isSignupAppearance ? SIGNUP_FIELD_COUNTRY_BUTTON_CLASSNAME : "border-input text-foreground flex h-12 items-center gap-2 rounded-md border bg-white px-3 type-body-sm";
  const t21 = hideCountrySelector ? "w-full" : "flex-1";
  const t22 = isSignupAppearance ? SIGNUP_FIELD_INPUT_CLASSNAME : undefined;
  const t23 = showError && "border-destructive ring-destructive placeholder:text-destructive ring-1";
  let t24;
  if ($[39] !== t21 || $[40] !== t22 || $[41] !== t23) {
    t24 = cn(t21, t22, t23);
    $[39] = t21;
    $[40] = t22;
    $[41] = t23;
    $[42] = t24;
  } else {
    t24 = $[42];
  }
  const phoneInputClassName = t24;
  const t25 = isSignupAppearance ? "border-mocha/20 rounded-[2px] border bg-white shadow-lg" : "border-input bg-card shadow-elevated rounded-md border";
  let t26;
  if ($[43] !== t25) {
    t26 = cn("absolute top-full left-0 z-20 mt-1 flex w-72 max-w-[calc(100vw-3rem)] flex-col overflow-hidden", t25);
    $[43] = t25;
    $[44] = t26;
  } else {
    t26 = $[44];
  }
  const countryMenuClassName = t26;
  const t27 = isSignupAppearance ? "gap-[var(--signup-field-label-gap,8px)]" : "gap-1";
  let t28;
  if ($[45] !== t27) {
    t28 = cn("InternationalPhoneField flex flex-col text-left", t27);
    $[45] = t27;
    $[46] = t28;
  } else {
    t28 = $[46];
  }
  const t29 = isSignupAppearance ? SIGNUP_FIELD_LABEL_CLASSNAME : undefined;
  let t30;
  if ($[47] !== fieldIds.inputId || $[48] !== fieldIds.labelId || $[49] !== label || $[50] !== showError || $[51] !== t29) {
    t30 = <Label className={t29} htmlFor={fieldIds.inputId} id={fieldIds.labelId} invalid={showError} text={label} />;
    $[47] = fieldIds.inputId;
    $[48] = fieldIds.labelId;
    $[49] = label;
    $[50] = showError;
    $[51] = t29;
    $[52] = t30;
  } else {
    t30 = $[52];
  }
  let t31;
  if ($[53] !== tooltip) {
    t31 = tooltip ? <TooltipProvider delayDuration={150}><Tooltip><TooltipTrigger asChild={true}><Info className="text-muted-foreground h-3.5 w-3.5 cursor-help" /></TooltipTrigger><TooltipContent><Text component="p" text={tooltip} variant="caption" /></TooltipContent></Tooltip></TooltipProvider> : null;
    $[53] = tooltip;
    $[54] = t31;
  } else {
    t31 = $[54];
  }
  let t32;
  if ($[55] !== t30 || $[56] !== t31) {
    t32 = <div className="flex items-center gap-1">{t30}{t31}</div>;
    $[55] = t30;
    $[56] = t31;
    $[57] = t32;
  } else {
    t32 = $[57];
  }
  const t33 = hideCountrySelector && "w-full";
  let t34;
  if ($[58] !== t33) {
    t34 = cn("flex gap-2", t33);
    $[58] = t33;
    $[59] = t34;
  } else {
    t34 = $[59];
  }
  let t35;
  if ($[60] !== activeOptionIndex || $[61] !== countryButtonClassName || $[62] !== countryEmptyLabel || $[63] !== countryMenuClassName || $[64] !== countryQuery || $[65] !== countrySearchLabel || $[66] !== fieldIds.inputId || $[67] !== handleCountrySearchKeyDown || $[68] !== handleCountrySelect || $[69] !== hideCountrySelector || $[70] !== isCountryMenuOpen || $[71] !== isSignupAppearance || $[72] !== label || $[73] !== openCountryMenu || $[74] !== selectedCountry.country || $[75] !== selectedCountry.dialCode || $[76] !== selectedCountry.id || $[77] !== selectedCountry.label || $[78] !== selectedCountry.name || $[79] !== visibleCountries) {
    t35 = hideCountrySelector ? null : <div className="relative" ref={countryMenuRef}><button aria-expanded={isCountryMenuOpen} aria-haspopup="listbox" aria-label={`${label} country code, ${selectedCountry.name} ${selectedCountry.dialCode}`} className={countryButtonClassName} ref={countryTriggerRef} type="button" onClick={() => isCountryMenuOpen ? closeCountryMenu({
        restoreFocus: false
      }) : openCountryMenu()}><FlagEmoji country={selectedCountry.country} /><Text component="span" text={isSignupAppearance ? selectedCountry.label : selectedCountry.dialCode} tone="muted" variant="caption" /><ChevronDown className="text-muted-foreground h-4 w-4" /></button>{isCountryMenuOpen ? <div className={countryMenuClassName}><div className="border-input flex items-center gap-2 border-b px-3 py-2"><Search aria-hidden={true} className="text-muted-foreground size-4 shrink-0" /><input aria-activedescendant={visibleCountries[activeOptionIndex] ? `${fieldIds.inputId}-country-${visibleCountries[activeOptionIndex].id}` : undefined} aria-autocomplete="list" aria-controls={`${fieldIds.inputId}-country-listbox`} aria-expanded={true} aria-label={countrySearchLabel} autoComplete="off" className="text-foreground placeholder:text-muted-foreground/60 type-body-sm w-full bg-transparent outline-none" placeholder={countrySearchLabel} ref={countrySearchRef} role="combobox" value={countryQuery} onChange={event_1 => {
            setCountryQuery(event_1.target.value);
            setActiveOptionIndex(0);
          }} onKeyDown={handleCountrySearchKeyDown} /></div><div className="max-h-56 overflow-y-auto py-1" id={`${fieldIds.inputId}-country-listbox`} ref={countryListRef} role="listbox">{visibleCountries.length === 0 ? <p className="text-muted-foreground type-body-sm px-3 py-3">{countryEmptyLabel}</p> : visibleCountries.map((country_4, index_1) => {
            const isSelected = country_4.id === selectedCountry.id;
            const isActive = index_1 === activeOptionIndex;
            return <div aria-selected={isSelected} className={cn("flex cursor-pointer items-center gap-2 px-3 py-2.5 text-left", isActive && "bg-primary/10")} data-option-index={index_1} id={`${fieldIds.inputId}-country-${country_4.id}`} key={country_4.id} role="option" aria-label={`${country_4.name} (${country_4.label}) ${country_4.dialCode}`} onClick={() => handleCountrySelect(country_4)} onMouseEnter={() => setActiveOptionIndex(index_1)}><FlagEmoji country={country_4.country} testId={`InternationalPhoneFieldCountryFlag-${country_4.id}`} /><span className="text-foreground type-body-sm flex-1 truncate">{country_4.name}</span><span className="text-muted-foreground type-body-sm tabular-nums">{country_4.dialCode}</span><Check aria-hidden={true} className={cn("text-primary size-4 shrink-0", !isSelected && "invisible")} /></div>;
          })}</div></div> : null}</div>;
    $[60] = activeOptionIndex;
    $[61] = countryButtonClassName;
    $[62] = countryEmptyLabel;
    $[63] = countryMenuClassName;
    $[64] = countryQuery;
    $[65] = countrySearchLabel;
    $[66] = fieldIds.inputId;
    $[67] = handleCountrySearchKeyDown;
    $[68] = handleCountrySelect;
    $[69] = hideCountrySelector;
    $[70] = isCountryMenuOpen;
    $[71] = isSignupAppearance;
    $[72] = label;
    $[73] = openCountryMenu;
    $[74] = selectedCountry.country;
    $[75] = selectedCountry.dialCode;
    $[76] = selectedCountry.id;
    $[77] = selectedCountry.label;
    $[78] = selectedCountry.name;
    $[79] = visibleCountries;
    $[80] = t35;
  } else {
    t35 = $[80];
  }
  const t36 = `${fieldIds.hintId} ${fieldIds.errorId}`;
  const t37 = fieldState.error?.message;
  const t38 = showError || undefined;
  const t39 = fieldIds.labelId;
  const t40 = selectedCountry.country === "US" || selectedCountry.country === "CA" ? "tel-national" : "tel";
  const t41 = fieldIds.inputId;
  const t42 = field.name;
  let t43;
  if ($[81] !== field.value || $[82] !== selectedCountry.country) {
    t43 = formatPhoneInputValue(field.value, selectedCountry.country);
    $[81] = field.value;
    $[82] = selectedCountry.country;
    $[83] = t43;
  } else {
    t43 = $[83];
  }
  let t44;
  if ($[84] !== field || $[85] !== name || $[86] !== selectedCountry.country || $[87] !== trigger) {
    t44 = () => {
      const raw = typeof field.value === "string" ? field.value : "";
      const normalized = normalizePhoneInputToE164IfPossible(raw, selectedCountry.country);
      if (normalized !== raw.trim()) {
        field.onChange(normalized);
      }
      field.onBlur();
      trigger(name);
    };
    $[84] = field;
    $[85] = name;
    $[86] = selectedCountry.country;
    $[87] = trigger;
    $[88] = t44;
  } else {
    t44 = $[88];
  }
  let t45;
  if ($[89] !== field || $[90] !== onInputChange || $[91] !== selectedCountry.country) {
    t45 = event_2 => {
      onInputChange?.(event_2);
      field.onChange(formatPhoneInputValue(event_2.target.value, selectedCountry.country));
    };
    $[89] = field;
    $[90] = onInputChange;
    $[91] = selectedCountry.country;
    $[92] = t45;
  } else {
    t45 = $[92];
  }
  let t46;
  if ($[93] !== field.name || $[94] !== fieldIds.inputId || $[95] !== fieldIds.labelId || $[96] !== phoneInputClassName || $[97] !== placeholder || $[98] !== t36 || $[99] !== t37 || $[100] !== t38 || $[101] !== t40 || $[102] !== t43 || $[103] !== t44 || $[104] !== t45) {
    t46 = <Input aria-describedby={t36} aria-errormessage={t37} aria-invalid={t38} aria-labelledby={t39} autoComplete={t40} className={phoneInputClassName} data-testid="InternationalPhoneFieldPhoneInput" id={t41} inputMode="tel" name={t42} placeholder={placeholder} type="tel" value={t43} onBlur={t44} onChange={t45} />;
    $[93] = field.name;
    $[94] = fieldIds.inputId;
    $[95] = fieldIds.labelId;
    $[96] = phoneInputClassName;
    $[97] = placeholder;
    $[98] = t36;
    $[99] = t37;
    $[100] = t38;
    $[101] = t40;
    $[102] = t43;
    $[103] = t44;
    $[104] = t45;
    $[105] = t46;
  } else {
    t46 = $[105];
  }
  let t47;
  if ($[106] !== t34 || $[107] !== t35 || $[108] !== t46) {
    t47 = <div className={t34}>{t35}{t46}</div>;
    $[106] = t34;
    $[107] = t35;
    $[108] = t46;
    $[109] = t47;
  } else {
    t47 = $[109];
  }
  const t48 = showError ? "pt-1" : isSignupAppearance ? undefined : "h-2";
  let t49;
  if ($[110] !== t48) {
    t49 = cn(t48);
    $[110] = t48;
    $[111] = t49;
  } else {
    t49 = $[111];
  }
  let t50;
  if ($[112] !== fieldIds.hintId || $[113] !== hint || $[114] !== showError) {
    t50 = hint && !showError ? <HintMessage id={fieldIds.hintId} text={hint} /> : null;
    $[112] = fieldIds.hintId;
    $[113] = hint;
    $[114] = showError;
    $[115] = t50;
  } else {
    t50 = $[115];
  }
  let t51;
  if ($[116] !== fieldIds.errorId || $[117] !== fieldIds.inputId || $[118] !== fieldState.error || $[119] !== showError) {
    t51 = showError && fieldState.error?.message ? <ErrorMessage htmlFor={fieldIds.inputId} id={fieldIds.errorId} text={fieldState.error.message} /> : null;
    $[116] = fieldIds.errorId;
    $[117] = fieldIds.inputId;
    $[118] = fieldState.error;
    $[119] = showError;
    $[120] = t51;
  } else {
    t51 = $[120];
  }
  let t52;
  if ($[121] !== t49 || $[122] !== t50 || $[123] !== t51) {
    t52 = <div className={t49}>{t50}{t51}</div>;
    $[121] = t49;
    $[122] = t50;
    $[123] = t51;
    $[124] = t52;
  } else {
    t52 = $[124];
  }
  let t53;
  if ($[125] !== t28 || $[126] !== t32 || $[127] !== t47 || $[128] !== t52) {
    t53 = <div className={t28} data-testid="InternationalPhoneField">{t32}{t47}{t52}</div>;
    $[125] = t28;
    $[126] = t32;
    $[127] = t47;
    $[128] = t52;
    $[129] = t53;
  } else {
    t53 = $[129];
  }
  return t53;
}
export const InternationalPhoneField = {
  RHF
};